import { FormNumberInput } from "@/components/atom/numberInput/form/FormNumberInput";
import { FormTextInput } from "@/components/atom/textInput/form/FormTextInput";
import { FormSubTitle } from "@/components/atom/title/form/FormSubTitle";
import { BarcodeInput } from "@/components/molecule/barcodeInput/BarcodeInput";
import { MantineSelectableTable } from "@/components/molecule/mantineSelectableTable/MantineSelectableTable";
import { InventoriesAutoComplete } from "@/components/organism/autoComplete/inventory/inventories-autoComplete";
import { LocationAutoComplete } from "@/components/organism/autoComplete/location/location-autoComplate";
import FormTemplate from "@/components/template/form/FormTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { useInventoriesFormFetch } from "@/fetch/form/useInventoriesFormFetch";
import { useRawMaterialHandler } from "@/hooks/handler/useRawMaterialHandler";
import { useWorkDataStore } from "@/store/work.store";
import { customNotification } from "@/utils/notificationShow";
import { Pagination } from "@mantine/core";
import { useForm } from "@mantine/form";
import { MRT_ColumnDef } from "mantine-react-table";

const RawMaterial = Object.assign(
  {},
  {
    Template: FormTemplate,
    SubTitle: FormSubTitle,
    TextInput: FormTextInput,
    NumberInput: FormNumberInput,
    BarcodeInput: BarcodeInput,
    Table: MantineSelectableTable,
    InventoriesAutoComplete: InventoriesAutoComplete,
    LocationAutoComplete: LocationAutoComplete,
    Pagination: Pagination,
  },
);

export const RawMaterialSelectLocationForm = () => {
  const { closeModal } = useModal();
  const { onSubmit } = useRawMaterialHandler();
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));
  const { work } = workData;
  const targetLocationCode = work?.locationSetting?.toLocation?.code;

  const routingBoms = work?.routingData?.routingBoms;
  const convertedRoutingBoms = routingBoms?.map((bom) => ({
    itemCode: bom.itemCode,
    itemName: bom.item?.name,
    itemCategoryName: bom.item?.itemCategoryName,
    routingCode: bom.routingCode,
  }));

  const form = useForm({
    initialValues: {
      useBarcode: false,
      barcodeInput: "",
      itemCode: "",
      workId: work?.id,
      worksWorkIdInputPutRequest: {
        lotId: "",
        sourceLocationCode: "",
        targetLocationCode: targetLocationCode,
        quantity: 0,
      },
    },
  });

  const { data, refetch } = useInventoriesFormFetch({
    itemCodes:
      form.values.itemCode ??
      convertedRoutingBoms?.map((bom) => bom.itemCode).join(","),
    locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
    lotId: form.values.barcodeInput,
  });

  const columns = () =>
    [
      {
        accessorKey: "itemCode",
        header: COMMON_TEXT.COLUMN.ITEM_CODE,
      },
      {
        accessorKey: "itemName",
        header: COMMON_TEXT.COLUMN.ITEM_NAME,
      },
      {
        accessorKey: "itemCategoryName",
        header: COMMON_TEXT.COLUMN.ITEM_CATEGORY_NAME,
      },
      {
        accessorKey: "routingCode",
        header: COMMON_TEXT.COLUMN.ROUTING_CODE,
      },
    ] as MRT_ColumnDef<any>[];

  const refetchHandler = async () => {
    const data = await refetch();
    const inventoryData = data.data.data;
    const noData = !inventoryData?.length;
    if (noData) {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue("worksWorkIdInputPutRequest.lotId", "");
      form.setFieldValue("worksWorkIdInputPutRequest.quantity", 0);
      customNotification.error({
        message: COMMON_TEXT.NOTIFICATION.LOT_NOT_FOUND,
      });
    } else {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue(
        "worksWorkIdInputPutRequest.lotId",
        form.values.barcodeInput,
      );
      form.setFieldValue(
        "worksWorkIdInputPutRequest.quantity",
        Number(inventoryData[0].quantity),
      );
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (!form.values.barcodeInput) return;
      form.setFieldValue("barcodeInput", form.values.barcodeInput);
      form.setFieldValue("useBarcode", true);
      refetchHandler();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) return;
    form.setFieldValue("barcodeInput", inputValue);
    form.setFieldValue("useBarcode", true);
    refetchHandler();
  };

  const handleOnSubmit = () => {
    const worksWorkIdInputPutRequest = {
      lotId: Number(form.values.worksWorkIdInputPutRequest.lotId),
      sourceLocationCode:
        form.values.worksWorkIdInputPutRequest.sourceLocationCode,
      targetLocationCode:
        form.values.worksWorkIdInputPutRequest.targetLocationCode,
      quantity: String(form.values.worksWorkIdInputPutRequest.quantity),
    };

    onSubmit({ worksWorkIdInputPutRequest: worksWorkIdInputPutRequest });
  };

  const handleOnRowSelection = (selectedRows: any) => {
    form.setFieldValue("itemCode", selectedRows[0]?.itemCode);
  };

  return (
    <RawMaterial.Template
      onSubmit={handleOnSubmit}
      onCancel={() => closeModal({})}
      isButtonDisabled={false}
    >
      <RawMaterial.Table
        enableMultiRowSelection={false}
        columns={columns()}
        data={convertedRoutingBoms ?? []}
        onRowSelection={handleOnRowSelection}
      />
      <RawMaterial.LocationAutoComplete
        withAsterisk
        label={COMMON_TEXT.LABEL.LOCATION}
        {...form.getInputProps("worksWorkIdInputPutRequest.sourceLocationCode")}
      />
      <RawMaterial.BarcodeInput
        disabled={!form.values.worksWorkIdInputPutRequest.sourceLocationCode}
        handleBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...form.getInputProps("barcodeInput")}
      />
      <RawMaterial.InventoriesAutoComplete
        disabled={!form.values.worksWorkIdInputPutRequest.sourceLocationCode}
        withAsterisk
        label={COMMON_TEXT.LABEL.LOT_INFORM}
        value={form.values.worksWorkIdInputPutRequest.lotId}
        query={
          form.values.itemCode
            ? {
                itemCode: form.values.itemCode,
                locationCode:
                  form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                quantity: { $gt: 0 },
              }
            : {
                itemCode: convertedRoutingBoms
                  ?.map((bom) => bom.itemCode)
                  .join(","),
                locationCode:
                  form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                quantity: { $gt: 0 },
              }
        }
        onChange={(value) => {
          const quantity = Number(
            data?.data?.filter((row) => row.lotId === Number(value))[0]
              ?.quantity ?? 0,
          );
          form.setFieldValue("useBarcode", false);
          form.setFieldValue("worksWorkIdInputPutRequest.quantity", quantity);
          form.setFieldValue("worksWorkIdInputPutRequest.lotId", value ?? "");
        }}
      />
      <RawMaterial.TextInput
        withAsterisk
        disabled
        label={COMMON_TEXT.LABEL.TARGET_LOCATION_CODE}
        value={targetLocationCode}
      />
      <RawMaterial.NumberInput
        withAsterisk
        label={COMMON_TEXT.COLUMN.QUANTITY}
        {...form.getInputProps("worksWorkIdInputPutRequest.quantity")}
      />
    </RawMaterial.Template>
  );
};
